import { useEffect, useState } from 'react';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  BarChart, Bar,
} from 'recharts';
import { DollarSign, TrendingUp, FileText, Loader2, Cpu, FlaskConical } from 'lucide-react';
import { analyticsApi } from '../../services/api';

interface Burn {
  window_days: number;
  total_cost_usd: number;
  daily_avg_usd: number;
  projected_month_usd: number;
  report_count: number;
  cost_per_report_usd: number;
}
interface Summary {
  total_cost_usd: number;
  total_calls: number;
  by_provider: Record<string, { calls: number; cost_usd: number; input_tokens: number; output_tokens: number; audio_seconds: number }>;
}
interface ModelRow { provider: string; model: string; calls: number; cost_usd: number; input_tokens: number; output_tokens: number }
interface OpRow { operation: string; calls: number; cost_usd: number; avg_latency_ms: number }
interface EnvRow { environment: string; calls: number; cost_usd: number }
interface DailyRow { date: string; anthropic: number; openai: number }

const usd = (n: number) => `$${(n ?? 0).toFixed(n < 1 ? 4 : 2)}`;

export default function CostAnalytics({ days }: { days: number }) {
  const [loading, setLoading] = useState(true);
  const [burn, setBurn] = useState<Burn | null>(null);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [models, setModels] = useState<ModelRow[]>([]);
  const [ops, setOps] = useState<OpRow[]>([]);
  const [envs, setEnvs] = useState<EnvRow[]>([]);
  const [daily, setDaily] = useState<DailyRow[]>([]);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      analyticsApi.costBurnRate(Math.min(days, 90)),
      analyticsApi.costSummary(days),
      analyticsApi.costByModel(days),
      analyticsApi.costByOperation(days),
      analyticsApi.costByEnvironment(days),
      analyticsApi.costDaily(days),
    ]).then(([b, s, m, o, e, d]) => {
      setBurn(b); setSummary(s); setModels(m); setOps(o); setEnvs(e); setDaily(d);
    }).finally(() => setLoading(false));
  }, [days]);

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-primary-500" /></div>;
  }

  const anthropic = summary?.by_provider?.anthropic;
  const openai = summary?.by_provider?.openai;

  return (
    <div className="space-y-6">
      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi icon={<TrendingUp size={18} />} label="Projected / month"
          value={usd(burn?.projected_month_usd || 0)} sub={`${usd(burn?.daily_avg_usd || 0)}/day avg`} accent />
        <Kpi icon={<DollarSign size={18} />} label={`Spend (${days}d)`}
          value={usd(summary?.total_cost_usd || 0)} sub={`${summary?.total_calls || 0} API calls`} />
        <Kpi icon={<FileText size={18} />} label="Cost / report"
          value={usd(burn?.cost_per_report_usd || 0)} sub={`${burn?.report_count || 0} reports`} />
        <Kpi icon={<Cpu size={18} />} label="Anthropic / OpenAI"
          value={`${usd(anthropic?.cost_usd || 0)}`} sub={`OpenAI ${usd(openai?.cost_usd || 0)}`} />
      </div>

      {/* Daily spend area chart */}
      <Card title="Daily spend by provider">
        {daily.length === 0 ? <Empty /> : (
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={daily} margin={{ left: -10 }}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${v}`} />
              <Tooltip formatter={(v: any) => usd(Number(v))} />
              <Legend />
              <Area type="monotone" dataKey="anthropic" stackId="1" stroke="#7c3aed" fill="#7c3aed" fillOpacity={0.3} />
              <Area type="monotone" dataKey="openai" stackId="1" stroke="#0d9488" fill="#0d9488" fillOpacity={0.3} />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* By operation */}
        <Card title="Cost by operation">
          {ops.length === 0 ? <Empty /> : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={ops} layout="vertical" margin={{ left: 30 }}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => `$${v}`} />
                <YAxis type="category" dataKey="operation" tick={{ fontSize: 11 }} width={110} />
                <Tooltip formatter={(v: any) => usd(Number(v))} />
                <Bar dataKey="cost_usd" fill="#3b82f6" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>

        {/* Environment split */}
        <Card title="Where the spend comes from">
          <div className="flex items-center gap-2 text-xs text-gray-400 mb-3">
            <FlaskConical size={14} /> production = real radiologist usage · local = dev/testing
          </div>
          {envs.length === 0 ? <Empty /> : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase text-gray-400 border-b border-gray-200 dark:border-surface-800">
                  <th className="py-2">Environment</th><th>Calls</th><th className="text-right">Cost</th>
                </tr>
              </thead>
              <tbody>
                {envs.sort((a, b) => b.cost_usd - a.cost_usd).map((e) => (
                  <tr key={e.environment} className="border-b border-gray-100 dark:border-surface-800/50">
                    <td className="py-2 font-medium">
                      <span className={`px-1.5 py-0.5 rounded text-xs ${
                        e.environment === 'production'
                          ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300'
                          : 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'
                      }`}>{e.environment}</span>
                    </td>
                    <td>{e.calls}</td>
                    <td className="text-right font-semibold">{usd(e.cost_usd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>

      {/* By model table */}
      <Card title="Cost by model">
        {models.length === 0 ? <Empty /> : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase text-gray-400 border-b border-gray-200 dark:border-surface-800">
                <th className="py-2">Model</th><th>Provider</th><th>Calls</th>
                <th className="text-right">In tok</th><th className="text-right">Out tok</th><th className="text-right">Cost</th>
              </tr>
            </thead>
            <tbody>
              {models.map((m) => (
                <tr key={m.provider + m.model} className="border-b border-gray-100 dark:border-surface-800/50">
                  <td className="py-2 font-medium text-gray-900 dark:text-white">{m.model}</td>
                  <td className="text-gray-500">{m.provider}</td>
                  <td>{m.calls}</td>
                  <td className="text-right text-gray-500">{m.input_tokens.toLocaleString()}</td>
                  <td className="text-right text-gray-500">{m.output_tokens.toLocaleString()}</td>
                  <td className="text-right font-semibold">{usd(m.cost_usd)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}

function Kpi({ icon, label, value, sub, accent }: { icon: React.ReactNode; label: string; value: string; sub?: string; accent?: boolean }) {
  return (
    <div className={`rounded-xl border p-4 ${accent
      ? 'border-primary-200 dark:border-primary-800/40 bg-primary-50 dark:bg-primary-900/20'
      : 'border-gray-200 dark:border-surface-800 bg-white dark:bg-surface-900'}`}>
      <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 text-xs mb-1">{icon}{label}</div>
      <div className="text-2xl font-bold text-gray-900 dark:text-white">{value}</div>
      {sub && <div className="text-xs text-gray-400 mt-0.5">{sub}</div>}
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-gray-200 dark:border-surface-800 bg-white dark:bg-surface-900 p-4">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">{title}</h3>
      {children}
    </div>
  );
}

function Empty() {
  return <p className="text-sm text-gray-400 dark:text-gray-500 py-8 text-center">No usage recorded in this window yet.</p>;
}
